'use strict';

// Keep this pure module identical to timely-client/src/platform/util/calendarSystem.js.
// Dates here are civil dates, never instants. Timezones are applied at the boundary.
const DAY_MS = 86400000;
const ETHIOPIAN_MONTHS = ['Meskerem', 'Tikimt', 'Hidar', 'Tahsas', 'Tir', 'Yekatit', 'Megabit', 'Miyazya', 'Ginbot', 'Sene', 'Hamle', 'Nehase', 'Pagume'];
const ethiopicFormatter = new Intl.DateTimeFormat('en-US-u-ca-ethiopic-nu-latn', { timeZone: 'UTC', year: 'numeric', month: 'numeric', day: 'numeric' });
function normalizeCalendar(value) {
  if (value == null || value === '') return 'gregory';
  if (value !== 'gregory' && value !== 'ethiopic') throw new RangeError('Unsupported calendar');
  return value;
}
function isoDate(year, month, day) {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function parseCivil(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (!match) throw new RangeError('Use a valid YYYY-MM-DD date');
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) throw new RangeError('Invalid date');
  return date;
}
function calendarParts(civilDate, calendar = 'gregory') {
  const date = parseCivil(civilDate);
  if (normalizeCalendar(calendar) === 'gregory') return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
  const parts = Object.fromEntries(ethiopicFormatter.formatToParts(date).map(part => [part.type, part.value]));
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
}
function daysInCalendarMonth(year, month, calendar = 'gregory') {
  if (normalizeCalendar(calendar) === 'ethiopic') return month === 13 ? (year % 4 === 3 ? 6 : 5) : 30;
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function fromCalendarDate(year, month, day, calendar = 'gregory') {
  calendar = normalizeCalendar(calendar);
  if (![year, month, day].every(Number.isInteger) || year < 1 || year > 9990 || month < 1 || month > (calendar === 'ethiopic' ? 13 : 12) || day < 1 || day > daysInCalendarMonth(year, month, calendar)) throw new RangeError('Invalid calendar date');
  if (calendar === 'gregory') return isoDate(year, month, day);
  // Meskerem 1, 2018 = September 11, 2025. Ethiopian leap years end in 3 modulo 4.
  const elapsed = 365 * (year - 2018) + Math.floor(year / 4) - Math.floor(2018 / 4) + 30 * (month - 1) + day - 1;
  const date = new Date(Date.UTC(2025, 8, 11) + elapsed * DAY_MS);
  return isoDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}
function addCivilDays(civilDate, amount) {
  const date = parseCivil(civilDate);
  date.setUTCDate(date.getUTCDate() + amount);
  return isoDate(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}
function calendarMonthRange(civilDate, calendar = 'gregory') {
  const { year, month } = calendarParts(civilDate, calendar);
  return { start: fromCalendarDate(year, month, 1, calendar), end: fromCalendarDate(year, month, daysInCalendarMonth(year, month, calendar), calendar) };
}
function addCalendarMonths(civilDate, amount, calendar = 'gregory') {
  const p = calendarParts(civilDate, calendar), count = calendar === 'ethiopic' ? 13 : 12;
  const index = p.year * count + p.month - 1 + amount;
  const year = Math.floor(index / count), month = ((index % count) + count) % count + 1;
  return fromCalendarDate(year, month, Math.min(p.day, daysInCalendarMonth(year, month, calendar)), calendar);
}
function formatCalendarDate(civilDate, calendar = 'gregory') {
  if (!civilDate) return '';
  if (calendar !== 'ethiopic') return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric' }).format(parseCivil(civilDate));
  const { year, month, day } = calendarParts(civilDate, calendar);
  return `${ETHIOPIAN_MONTHS[month - 1]} ${day}, ${year} E.C.`;
}
function matchesCalendarRecurrence(date, start, unit, interval = 1, calendar = 'gregory', weeklyAvailability = false) {
  if (date < start) return false;
  interval = Number(interval) || 1;
  if (!Number.isInteger(interval) || interval < 1) return false;
  const elapsed = Math.round((parseCivil(date) - parseCivil(start)) / DAY_MS);
  if (unit === 'none') return true;
  if (unit === 'day') return elapsed % interval === 0;
  if (unit === 'week') {
    if (!weeklyAvailability) return elapsed % (7 * interval) === 0;
    const mondayOffset = (parseCivil(start).getUTCDay() + 6) % 7;
    return Math.floor((elapsed + mondayOffset) / 7) % interval === 0;
  }
  const a = calendarParts(start, calendar), b = calendarParts(date, calendar);
  if (unit === 'month') return ((b.year - a.year) * (calendar === 'ethiopic' ? 13 : 12) + b.month - a.month) % interval === 0 && a.day === b.day;
  if (unit === 'year') return (b.year - a.year) % interval === 0 && a.month === b.month && a.day === b.day;
  return false;
}
module.exports = { ETHIOPIAN_MONTHS, normalizeCalendar, isoDate, parseCivil, calendarParts, daysInCalendarMonth, fromCalendarDate, addCivilDays, calendarMonthRange, addCalendarMonths, formatCalendarDate, matchesCalendarRecurrence };
